import { Component, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../../controls/components/basic/button/button.component';
import { ButtonTertieryDirective } from '../../../controls/directives/button-tertiery.directive';
import { PartnerOnboardingPreset } from '../../models/partner-onboarding-preset';
import { PartnerOnboardingService } from '../../services/partner-onboarding.service';
import { GenericModalService, ModalSettingsTemplate } from '../../../shared/generic-modal/generic-modal.service';
import { ToastService, ToastType } from '../../../shared/toast/toast.service';
import { PresetNameDialogComponent } from '../partner-onboarding-preset-name-dialog/partner-onboarding-preset-name-dialog.component';

export enum PresetManagementResult {
    Cancelled,
    PresetsModified
}

@Component({
    selector: 'app-partner-onboarding-preset-manage-dialog',
    standalone: true,
    templateUrl: './partner-onboarding-preset-manage-dialog.component.html',
    styleUrl: './partner-onboarding-preset-manage-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    ButtonComponent,
    ButtonTertieryDirective
]
})
export class PresetManagementDialogComponent implements OnInit {
    public presets = signal<PartnerOnboardingPreset[]>([]);
    public isLoading = signal<boolean>(false);
    private hasChanges = false;

    constructor(
        private activeModal: NgbActiveModal,
        private partnerOnboardingService: PartnerOnboardingService,
        private modalService: GenericModalService,
        private toastService: ToastService
    ) {}

    ngOnInit() {
        void this.loadPresets();
    }

    private async loadPresets(): Promise<void> {
        try {
            this.isLoading.set(true);
            const presets = await this.partnerOnboardingService.getPartnerOnboardingPresets();
            const sortedPresets = this.sortPresetsByName(presets || []);
            this.presets.set(sortedPresets);
        } catch (error) {
            this.toastService.showToast('Failed to load presets!', ToastType.error);
            console.error('Error loading presets:', error);
        } finally {
            this.isLoading.set(false);
        }
    }

    private sortPresetsByName(presets: PartnerOnboardingPreset[]): PartnerOnboardingPreset[] {
    return presets.sort((a, b) => a.Name.localeCompare(b.Name));
}

    async renamePreset(preset: PartnerOnboardingPreset): Promise<void> {
        try {
            const modalSettings = new ModalSettingsTemplate('md', 'static');
            
            const existingNames = this.presets()
                .filter(p => p.Id !== preset.Id)
                .map(p => p.Name);
            
            const newName = await this.modalService.openComponentWithInitialize<string, PresetNameDialogComponent>(
                PresetNameDialogComponent, 
                modalSettings,
                (dialogComponent) => {
                    dialogComponent.setExistingNames(existingNames);
                    dialogComponent.setExistingName(preset.Name);
                }
            );

            if (newName && newName.trim() !== preset.Name) {
                const updatedPreset = { ...preset, Name: newName.trim() };
                await this.partnerOnboardingService.savePartnerOnboardingPreset(updatedPreset);
                
                const updatedPresets = this.presets().map(p => 
                    p.Id === preset.Id ? updatedPreset : p
                );
                const sortedPresets = this.sortPresetsByName(updatedPresets);
                this.presets.set(sortedPresets);
                
                this.hasChanges = true;
                this.toastService.showToast('Preset renamed successfully!', ToastType.success);
            }
            
        } catch { /* empty */ }
    }

    async deletePreset(preset: PartnerOnboardingPreset): Promise<void> {
        try {
            await this.modalService.confirmDirect(
                'Delete Preset',
                `Are you sure you want to delete the preset "${preset.Name}"? This action cannot be undone.`,
                'Delete',
                'Cancel'
            );

            await this.partnerOnboardingService.deletePartnerOnboardingPreset(preset.Id);
            
            const updatedPresets = this.presets().filter(p => p.Id !== preset.Id);
            this.presets.set(updatedPresets);
            
            this.hasChanges = true;
            this.toastService.showToast('Preset deleted successfully!', ToastType.success);
            
        } catch { /* empty */ }
    }

    close(): void {
        const result = this.hasChanges 
            ? PresetManagementResult.PresetsModified 
            : PresetManagementResult.Cancelled;
        this.activeModal.close(result);
    }

    get hasPresets(): boolean {
        return this.presets().length > 0;
    }
}
